import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Button, Card, Col, DatePicker, Drawer, Form, Input, Modal, Row, Select, Space, Statistic,
    Table, Tag, Tooltip, Typography, message,
} from "antd";
import {
    PlusOutlined, FileExcelOutlined, FilePdfOutlined, CheckCircleOutlined,
    LockOutlined, UnlockOutlined, EyeOutlined, StarFilled, StarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { NAVY, drawEtatHeaderFooter, finalizeEtatPdf, loadEntete, loadLogos, loadLogoCompagnieConnectee }
    from "../../stats/sinistre/etatsSinistresFunctions";
import axiosInstance from "../../../variables/api/axiosInstance";
import { displayNumbers } from "../../../variables/numbersFunctions";
import { getActiveInventaire, setActiveInventaire } from "../../../variables/activeInventaire";
import SideBar from "../../../components/SideBar";
import NavBar from "../../../components/NavBar";
import { rightsService } from "../../../services/rightsService";

const CAN_WRITE = () => rightsService("HSI117"); // écriture : créer / clôturer / rouvrir

const { Title, Text } = Typography;

const fmt = (v) => displayNumbers(Math.round(Number(v) || 0));
// Formateur PDF-safe : jsPDF (police standard) ne connaît pas l'espace fine insécable (U+202F) que
// produit toLocaleString/displayNumbers → elle s'affiche « / ». On groupe avec une espace ASCII.
const fmtPdf = (v) => {
    const n = Math.round(Number(v) || 0);
    return (n < 0 ? "-" : "") + String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};
const sensTag = (sens) => {
    if (sens === "BONUS" || sens === "BONI") return <Tag color="green">{sens}</Tag>;
    if (sens === "MALUS" || sens === "MALI") return <Tag color="red">{sens}</Tag>;
    return <Tag>NEUTRE</Tag>;
};

export default function Inventaires() {
    const [inventaires, setInventaires] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [form] = Form.useForm();
    const [active, setActive] = useState(getActiveInventaire());
    const [reportOpen, setReportOpen] = useState(false);
    const [report, setReport] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);
    const canWrite = CAN_WRITE(); // HSI117 — créer / clôturer / rouvrir

    const charger = useCallback(() => {
        setLoading(true);
        axiosInstance.get("/api/inventaire")
            .then((res) => setInventaires(Array.isArray(res.data) ? res.data : []))
            .catch(() => message.error("Impossible de charger les inventaires."))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { charger(); }, [charger]);

    useEffect(() => {
        axiosInstance.get("/api/branche")
            .then((res) => setBranches(Array.isArray(res.data) ? res.data : []))
            .catch(() => setBranches([]));
    }, []);

    // Libellé d'une branche depuis son code (repli sur le code). Regroupement des lignes par branche.
    const branchLabel = useCallback((code) =>
        (branches.find((b) => b.codeBranche === code)?.nom) || code || "—", [branches]);
    const groupByBranche = useCallback((lignes) => {
        const m = new Map();
        (lignes || []).forEach((l) => {
            const k = l.branche || "—";
            if (!m.has(k)) m.set(k, []);
            m.get(k).push(l);
        });
        return Array.from(m.entries())
            .sort((a, b) => branchLabel(a[0]).localeCompare(branchLabel(b[0])))
            .map(([code, ls]) => ({ code, lignes: ls, subtotal: ls.reduce((s, l) => s + (Number(l.delta) || 0), 0) }));
    }, [branchLabel]);
    // Sens d'un sous-total : principal → baisse=BONI ; recours (inversé) → hausse=BONI.
    const subSens = (delta, recours) => {
        if (Math.abs(delta) <= 0.5) return "NEUTRE";
        const boni = recours ? delta > 0 : delta < 0;
        return boni ? "BONI" : "MALI";
    };

    const creer = () => {
        form.validateFields().then((vals) => {
            const payload = {
                titre: vals.titre,
                description: vals.description || null,
                // Le périmètre compagnie est TOUJOURS l'organisme du créateur (imposé côté backend).
                brancheCode: vals.brancheCode || null,
                dateDebut: vals.periode?.[0] ? vals.periode[0].format("YYYY-MM-DD") : null,
                dateFin: vals.periode?.[1] ? vals.periode[1].format("YYYY-MM-DD") : null,
            };
            axiosInstance.post("/api/inventaire", payload)
                .then(() => { message.success("Inventaire créé."); setCreateOpen(false); form.resetFields(); charger(); })
                .catch((e) => message.error(e?.response?.data?.message || "Échec de la création."));
        });
    };

    const cloturer = (inv) => {
        Modal.confirm({
            title: "Clôturer l'inventaire ?",
            content: `« ${inv.titre} » ne recevra plus de nouvelles réévaluations tant qu'il reste clôturé.`,
            okText: "Clôturer", cancelText: "Annuler",
            onOk: () => axiosInstance.post(`/api/inventaire/${inv.codeId}/cloturer`)
                .then(() => { message.success("Inventaire clôturé."); charger(); })
                .catch(() => message.error("Échec de la clôture.")),
        });
    };

    const rouvrir = (inv) => {
        axiosInstance.post(`/api/inventaire/${inv.codeId}/rouvrir`)
            .then(() => { message.success("Inventaire rouvert."); charger(); })
            .catch(() => message.error("Échec de la réouverture."));
    };

    const definirActif = (inv) => {
        if (active?.codeId === inv.codeId) {
            setActiveInventaire(null); setActive(null); message.info("Inventaire actif retiré.");
        } else {
            setActiveInventaire(inv); setActive({ codeId: inv.codeId, titre: inv.titre });
            message.success(`« ${inv.titre} » est désormais l'inventaire actif : vos réévaluations s'y rattachent.`);
        }
    };

    const voirRapport = (inv) => {
        setReportOpen(true); setReport(null); setReportLoading(true);
        axiosInstance.get(`/api/inventaire/${inv.codeId}/rapport`)
            .then((res) => setReport(res.data))
            .catch(() => message.error("Impossible de charger le rapport."))
            .finally(() => setReportLoading(false));
    };

    const columns = [
        { title: "Titre", dataIndex: "titre", key: "titre", render: (t, r) => (
            <Space>
                <Text strong>{t}</Text>
                {active?.codeId === r.codeId && <Tag color="blue">Actif</Tag>}
            </Space>
        ) },
        { title: "Période", key: "periode", render: (_, r) =>
            `${r.dateDebut ? dayjs(r.dateDebut).format("DD/MM/YYYY") : "—"} → ${r.dateFin ? dayjs(r.dateFin).format("DD/MM/YYYY") : "—"}` },
        { title: "Compagnie", dataIndex: "organismeCode", key: "organismeCode", render: (c) => c || "—" },
        { title: "Branche", key: "branche", render: (_, r) => r.brancheCode ? branchLabel(r.brancheCode) : "Toutes" },
        { title: "Statut", dataIndex: "statut", key: "statut", render: (s) =>
            s === "OUVERT" ? <Tag color="green">Ouvert</Tag> : <Tag color="default">Clôturé</Tag> },
        { title: "Créé par", dataIndex: "createdBy", key: "createdBy" },
        { title: "Actions", key: "actions", render: (_, r) => (
            <Space wrap>
                <Tooltip title="Voir le rapport boni/mali">
                    <Button size="small" icon={<EyeOutlined />} onClick={() => voirRapport(r)}>Rapport</Button>
                </Tooltip>
                <Tooltip title={active?.codeId === r.codeId ? "Retirer comme actif" : "Définir comme inventaire actif"}>
                    <Button size="small" disabled={r.statut !== "OUVERT"}
                        icon={active?.codeId === r.codeId ? <StarFilled /> : <StarOutlined />}
                        onClick={() => definirActif(r)}>
                        {active?.codeId === r.codeId ? "Actif" : "Activer"}
                    </Button>
                </Tooltip>
                {canWrite && (r.statut === "OUVERT"
                    ? <Button size="small" icon={<LockOutlined />} onClick={() => cloturer(r)}>Clôturer</Button>
                    : <Button size="small" icon={<UnlockOutlined />} onClick={() => rouvrir(r)}>Rouvrir</Button>)}
            </Space>
        ) },
    ];

    const ligneCols = (recours, showBranche = false) => [
        { title: "N° sinistre", dataIndex: "numSinistre", key: "numSinistre" },
        { title: "Assuré", dataIndex: "assure", key: "assure" },
        { title: "Compagnie", dataIndex: "organismeNom", key: "organismeNom" },
        ...(showBranche ? [{ title: "Branche", dataIndex: "branche", key: "branche", render: (c) => branchLabel(c) }] : []),
        { title: "Prov. avant", dataIndex: "provisionAvant", key: "avant", align: "right", render: fmt },
        { title: "Prov. après", dataIndex: "provisionApres", key: "apres", align: "right", render: fmt },
        { title: "Écart", dataIndex: "delta", key: "delta", align: "right",
          render: (v) => <Text type={v < 0 ? "success" : v > 0 ? "danger" : undefined}>{fmt(v)}</Text> },
        { title: recours ? "Sens (recours)" : "Sens", dataIndex: "sens", key: "sens", render: sensTag },
        { title: "Réviseur(s)", dataIndex: "reviseurs", key: "reviseurs", render: (r) => (r || []).join(", ") },
    ];

    // Rendu d'une section (principal/recours) REGROUPÉE par branche, avec sous-total par branche.
    const sectionGroupee = (lignes, recours) => {
        const groupes = groupByBranche(lignes);
        if (!groupes.length) {
            return <Text type="secondary">{recours ? "Aucun recours réévalué dans cet inventaire." : "Aucun dossier réévalué."}</Text>;
        }
        const cols = ligneCols(recours, false);
        return groupes.map((g) => (
            <div key={`${recours ? "r" : "p"}-${g.code}`} style={{ marginBottom: 14 }}>
                <Text strong style={{ display: "block", marginBottom: 4 }}>
                    Branche : {branchLabel(g.code)} <Tag>{g.lignes.length} dossier(s)</Tag>
                </Text>
                <Table rowKey={(r) => `${recours ? "r" : "p"}-${g.code}-${r.sinistreCodeId || r.numSinistre}`}
                    size="small" pagination={false} columns={cols} dataSource={g.lignes}
                    summary={() => (
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={cols.length - 3}><Text strong>Sous-total {branchLabel(g.code)}</Text></Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="right" colSpan={2}><Text strong>{fmt(g.subtotal)}</Text></Table.Summary.Cell>
                            <Table.Summary.Cell index={2}>{sensTag(subSens(g.subtotal, recours))}</Table.Summary.Cell>
                            <Table.Summary.Cell index={3} />
                        </Table.Summary.Row>
                    )} />
            </div>
        ));
    };

    // ── Exports ─────────────────────────────────────────────────────────────
    const exportExcel = () => {
        if (!report) return;
        const wb = XLSX.utils.book_new();
        const meta = [
            ["Inventaire", report.titre],
            ["Période", `${report.dateDebut || "—"} → ${report.dateFin || "—"}`],
            ["Statut", report.statut],
            ["Participants", (report.participants || []).join(", ")],
            [],
        ];
        const secToAoa = (titre, lignes, recours) => {
            const rows = [[titre]];
            const groupes = groupByBranche(lignes);
            if (!groupes.length) { rows.push(["(aucun dossier)"]); return rows; }
            groupes.forEach((g) => {
                rows.push([], [`Branche : ${branchLabel(g.code)} (${g.lignes.length} dossier(s))`]);
                rows.push(["N° sinistre", "Assuré", "Compagnie", "Prov. avant", "Prov. après", "Écart", "Sens", "Réviseurs"]);
                g.lignes.forEach((l) => rows.push([
                    l.numSinistre, l.assure, l.organismeNom,
                    Math.round(l.provisionAvant || 0), Math.round(l.provisionApres || 0), Math.round(l.delta || 0),
                    l.sens, (l.reviseurs || []).join(", "),
                ]));
                rows.push([`Sous-total ${branchLabel(g.code)}`, "", "", "", "", Math.round(g.subtotal), subSens(g.subtotal, recours)]);
            });
            return rows;
        };
        const aRecours = (report.dossiersRecours || []).length > 0;
        const aoa = [
            ...meta,
            ...secToAoa("SECTION PRINCIPAL (charge)", report.dossiersPrincipal, false),
            [], ["Σ Bonis principal", Math.round(report.sommeBonisPrincipal || 0)],
            ["Σ Malis principal", Math.round(report.sommeMalisPrincipal || 0)],
            ["Net principal", Math.round(report.netPrincipal || 0), report.sensPrincipal],
            [],
            // Section recours UNIQUEMENT s'il y a au moins un recours réévalué.
            ...(aRecours ? [
                ...secToAoa("SECTION RECOURS (sens inversé)", report.dossiersRecours, true),
                [], ["Σ Bonis recours", Math.round(report.sommeBonisRecours || 0)],
                ["Σ Malis recours", Math.round(report.sommeMalisRecours || 0)],
                ["Net recours (sens charge)", Math.round(report.netRecoursCharge || 0), report.sensRecours],
                [],
            ] : []),
            ["RÉSULTAT GÉNÉRAL", Math.round(report.resultatNet || 0), report.sensGeneral],
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), "Rapport");
        const vent = [["Ventilation par utilisateur"], ["Utilisateur", "Net principal", "Net recours (charge)", "Résultat", "Nb dossiers"]];
        (report.parUtilisateur || []).forEach((v) => vent.push([v.cle, Math.round(v.netPrincipal || 0), Math.round(v.netRecoursCharge || 0), Math.round(v.resultatNet || 0), v.nbDossiers]));
        vent.push([], ["Ventilation par compagnie / branche"], ["Clé", "Net principal", "Net recours (charge)", "Résultat", "Nb dossiers"]);
        (report.parCompagnieBranche || []).forEach((v) => vent.push([v.cle, Math.round(v.netPrincipal || 0), Math.round(v.netRecoursCharge || 0), Math.round(v.resultatNet || 0), v.nbDossiers]));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(vent), "Ventilations");
        XLSX.writeFile(wb, `Inventaire_${(report.titre || "").replace(/\s+/g, "_")}.xlsx`);
    };

    const exportPDF = async () => {
        if (!report) return;
        const doc = new jsPDF({ orientation: "portrait" });
        let enteteData = null, logos = [], logoCompagnie = null;
        try {
            [enteteData, logos, logoCompagnie] = await Promise.all([loadEntete(), loadLogos(), loadLogoCompagnieConnectee()]);
        } catch (e) { /* entête/logos optionnels */ }
        const formattedNow = new Date().toLocaleString("fr-FR",
            { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

        const NAVY_FILL = NAVY || [31, 58, 95];
        const GREY = [232, 234, 246];
        const spanTitre = (txt, fill) => [{ content: txt, colSpan: 7,
            styles: { fontStyle: "bold", fillColor: fill || NAVY_FILL, textColor: fill ? 20 : 255 } }];
        const sousTotal = (g, recours) => [
            { content: `Sous-total ${branchLabel(g.code)}`, colSpan: 5, styles: { halign: "right", fontStyle: "bold", fillColor: GREY } },
            { content: fmtPdf(g.subtotal), styles: { halign: "right", fontStyle: "bold", fillColor: GREY } },
            { content: subSens(g.subtotal, recours), styles: { fontStyle: "bold", fillColor: GREY } },
        ];

        const body = [];
        body.push([{ content: `Inventaire : ${report.titre || ""}    —    Participants : ${(report.participants || []).join(", ") || "—"}`,
            colSpan: 7, styles: { fontStyle: "bold", fillColor: [250, 250, 250] } }]);

        // Section PRINCIPAL (toujours affichée).
        body.push(spanTitre("SECTION PRINCIPAL (CHARGE) — PAR BRANCHE"));
        groupByBranche(report.dossiersPrincipal).forEach((g) => {
            body.push(spanTitre(`Branche : ${branchLabel(g.code)} (${g.lignes.length} dossier(s))`, GREY));
            g.lignes.forEach((l) => body.push([l.numSinistre || "", l.assure || "", l.organismeNom || "",
                fmtPdf(l.provisionAvant), fmtPdf(l.provisionApres), fmtPdf(l.delta), l.sens || ""]));
            body.push(sousTotal(g, false));
        });
        body.push([{ content: `NET PRINCIPAL (toutes branches) : ${fmtPdf(report.netPrincipal)}  (${report.sensPrincipal})`,
            colSpan: 7, styles: { fontStyle: "bold", fillColor: [246, 255, 237] } }]);

        // Section RECOURS : UNIQUEMENT s'il y a au moins un recours réévalué.
        if ((report.dossiersRecours || []).length) {
            body.push(spanTitre("SECTION RECOURS (SENS INVERSÉ) — PAR BRANCHE"));
            groupByBranche(report.dossiersRecours).forEach((g) => {
                body.push(spanTitre(`Branche : ${branchLabel(g.code)} (${g.lignes.length} dossier(s))`, GREY));
                g.lignes.forEach((l) => body.push([l.numSinistre || "", l.assure || "", l.organismeNom || "",
                    fmtPdf(l.provisionAvant), fmtPdf(l.provisionApres), fmtPdf(l.delta), l.sens || ""]));
                body.push(sousTotal(g, true));
            });
            body.push([{ content: `NET RECOURS (sens charge) : ${fmtPdf(report.netRecoursCharge)}  (${report.sensRecours})`,
                colSpan: 7, styles: { fontStyle: "bold", fillColor: [255, 247, 230] } }]);
        }

        body.push([{ content: `RÉSULTAT GÉNÉRAL : ${fmtPdf(report.resultatNet)}  =  ${report.sensGeneral}`,
            colSpan: 7, styles: { fontStyle: "bold", halign: "center", fillColor: [255, 241, 240], textColor: 20 } }]);

        autoTable(doc, {
            head: [["N° sinistre", "Assuré", "Compagnie", "Avant", "Après", "Écart", "Sens"]],
            body,
            theme: "grid",
            styles: { fontSize: 7.5, cellPadding: 1.4, overflow: "linebreak", valign: "middle" },
            headStyles: { fillColor: NAVY_FILL, textColor: 255, fontSize: 8, fontStyle: "bold" },
            columnStyles: { 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" } },
            margin: { top: 34, bottom: 22, left: 14, right: 14 },
            didDrawPage: () => drawEtatHeaderFooter(doc, {
                title: "RAPPORT D'INVENTAIRE — BONIS / MALIS",
                subtitle: `${report.titre || ""} · du ${report.dateDebut || "—"} au ${report.dateFin || "—"} · Statut : ${report.statut || ""}`,
                enteteData, logos, formattedNow, logoCompagnie,
                footerContact: true,   // pied aligné sur les Conditions Particulières
            }),
        });
        finalizeEtatPdf(doc);
        doc.save(`Inventaire_${(report.titre || "").replace(/\s+/g, "_")}.pdf`);
    };

    const totalsCards = useMemo(() => report ? (
        <Row gutter={12} style={{ marginBottom: 12 }}>
            <Col span={8}><Card size="small"><Statistic title="Σ Bonis (principal)" value={fmt(report.sommeBonisPrincipal)} valueStyle={{ color: "#237804" }} /></Card></Col>
            <Col span={8}><Card size="small"><Statistic title="Σ Malis (principal)" value={fmt(report.sommeMalisPrincipal)} valueStyle={{ color: "#a8071a" }} /></Card></Col>
            <Col span={8}><Card size="small"><Statistic title="Résultat général" value={fmt(report.resultatNet)} suffix={report.sensGeneral} valueStyle={{ color: report.resultatNet < 0 ? "#237804" : report.resultatNet > 0 ? "#a8071a" : undefined }} /></Card></Col>
        </Row>
    ) : null, [report]);

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"SINISTRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
            <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                <Col><Title level={3} style={{ margin: 0 }}>Inventaires de réévaluation</Title></Col>
                {canWrite && <Col><Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>Nouvel inventaire</Button></Col>}
            </Row>

            {active?.codeId && (
                <Card size="small" style={{ marginBottom: 12, background: "#e6f4ff", borderColor: "#91caff" }}>
                    <Space>
                        <CheckCircleOutlined style={{ color: "#1677ff" }} />
                        <Text>Inventaire actif : <Text strong>{active.titre}</Text> — vos réévaluations s'y rattachent.</Text>
                        <Button size="small" onClick={() => { setActiveInventaire(null); setActive(null); }}>Retirer</Button>
                    </Space>
                </Card>
            )}

            <Table rowKey="codeId" size="small" loading={loading} columns={columns} dataSource={inventaires}
                pagination={{ pageSize: 12, showSizeChanger: true }} />

            <Modal open={createOpen} title="Nouvel inventaire" onCancel={() => setCreateOpen(false)} onOk={creer} okText="Créer">
                <Form form={form} layout="vertical">
                    <Form.Item name="titre" label="Titre" rules={[{ required: true, message: "Titre requis" }]}>
                        <Input placeholder="Ex. Inventaire trimestriel T3 2026" />
                    </Form.Item>
                    <Form.Item name="periode" label="Période (indicative)">
                        <DatePicker.RangePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                    </Form.Item>
                    <Form.Item name="brancheCode" label="Branche (optionnel)"
                        extra="L'inventaire porte sur VOS propres dossiers. Laissez vide = toutes les branches.">
                        <Select allowClear showSearch optionFilterProp="label" placeholder="Toutes les branches"
                            options={(branches || []).map((b) => ({ value: b.codeBranche, label: b.nom || b.codeBranche }))} />
                    </Form.Item>
                    <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
                </Form>
            </Modal>

            <Drawer open={reportOpen} onClose={() => setReportOpen(false)} width={1100}
                title={report ? `Rapport — ${report.titre}` : "Rapport d'inventaire"}>
                {reportLoading && <Text>Chargement…</Text>}
                {report && (
                    <>
                        {/* Barre d'export en HAUT DU CORPS (l'en-tête du Drawer peut être masqué par le
                            bandeau fixe « ENVIRONNEMENT DE TEST »). Collante pour rester visible au défilement. */}
                        <div style={{ position: "sticky", top: 0, zIndex: 5, background: "#fff", padding: "4px 0 10px",
                            marginBottom: 4, borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "flex-end", gap: 8 }}>
                            <Button icon={<FilePdfOutlined />} onClick={exportPDF}>Exporter PDF</Button>
                            <Button icon={<FileExcelOutlined />} onClick={exportExcel} style={{ borderColor: "#059669", color: "#059669" }}>Exporter Excel</Button>
                        </div>
                        {totalsCards}
                        <Title level={5}>Section principal (charge) — par branche</Title>
                        {sectionGroupee(report.dossiersPrincipal, false)}
                        <Card size="small" style={{ marginBottom: 8, background: "#f6ffed", borderColor: "#b7eb8f" }}>
                            <Space><Text strong>Net principal (toutes branches) :</Text><Text strong>{fmt(report.netPrincipal)}</Text>{sensTag(report.sensPrincipal)}</Space>
                        </Card>

                        {(report.dossiersRecours || []).length > 0 && (
                            <>
                                <Title level={5} style={{ marginTop: 16 }}>Section recours (sens inversé) — par branche</Title>
                                {sectionGroupee(report.dossiersRecours, true)}
                                <Card size="small" style={{ marginBottom: 8, background: "#fff7e6", borderColor: "#ffd591" }}>
                                    <Space><Text strong>Net recours (sens charge) :</Text><Text strong>{fmt(report.netRecoursCharge)}</Text>{sensTag(report.sensRecours)}</Space>
                                </Card>
                            </>
                        )}

                        <Card size="small" style={{ marginTop: 16, background: "#fafafa" }}>
                            <Space size="large">
                                <Statistic title="Résultat général" value={fmt(report.resultatNet)} />
                                <div style={{ alignSelf: "center" }}>{sensTag(report.sensGeneral)}</div>
                            </Space>
                        </Card>

                        <Row gutter={16} style={{ marginTop: 16 }}>
                            <Col span={12}>
                                <Title level={5}>Par utilisateur</Title>
                                <Table rowKey="cle" size="small" pagination={false} dataSource={report.parUtilisateur || []}
                                    columns={[
                                        { title: "Utilisateur", dataIndex: "cle" },
                                        { title: "Net principal", dataIndex: "netPrincipal", align: "right", render: fmt },
                                        { title: "Net recours", dataIndex: "netRecoursCharge", align: "right", render: fmt },
                                        { title: "Résultat", dataIndex: "resultatNet", align: "right", render: (v) => <Text strong>{fmt(v)}</Text> },
                                        { title: "Dossiers", dataIndex: "nbDossiers", align: "right" },
                                    ]} />
                            </Col>
                            <Col span={12}>
                                <Title level={5}>Par compagnie / branche</Title>
                                <Table rowKey="cle" size="small" pagination={false} dataSource={report.parCompagnieBranche || []}
                                    columns={[
                                        { title: "Compagnie / branche", dataIndex: "cle" },
                                        { title: "Net principal", dataIndex: "netPrincipal", align: "right", render: fmt },
                                        { title: "Net recours", dataIndex: "netRecoursCharge", align: "right", render: fmt },
                                        { title: "Résultat", dataIndex: "resultatNet", align: "right", render: (v) => <Text strong>{fmt(v)}</Text> },
                                        { title: "Dossiers", dataIndex: "nbDossiers", align: "right" },
                                    ]} />
                            </Col>
                        </Row>
                    </>
                )}
            </Drawer>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
